import type { ClientResource } from './types';

/**
 * Opdrachtgever: een particulier, een bedrijf of een makelaar. Het type staat
 * erbij omdat dat bepaalt wie het rapport krijgt.
 */
export const CLIENT_TYPES = ['private', 'business', 'agency'] as const;

export type ClientType = (typeof CLIENT_TYPES)[number];

/** Gelijk aan `ClientType::label()` in de API. */
export const CLIENT_TYPE_LABELS: Record<ClientType, string> = {
    private: 'Particulier',
    business: 'Zakelijk',
    agency: 'Makelaar',
};

/** Het correspondentieadres van de klant; los van het adres van de keuring. */
export interface ClientAddress {
    street: string | null;
    number: string | null;
    suffix: string | null;
    postcode: string | null;
    city: string | null;
}

export interface Client {
    uuid: string;
    type: string;
    typeLabel: string;
    name: string;
    contactName: string | null;
    email: string | null;
    phone: string | null;
    address: ClientAddress;
    notes: string | null;
    externalReference: string | null;
}

export function toClient(resource: ClientResource): Client {
    return {
        uuid: resource.uuid,
        type: resource.type,
        typeLabel: resource.type_label,
        name: resource.name,
        contactName: resource.contact_name,
        email: resource.email,
        phone: resource.phone,
        address: {
            street: resource.address.street,
            number: resource.address.number,
            suffix: resource.address.suffix,
            postcode: resource.address.postcode,
            city: resource.address.city,
        },
        notes: resource.notes,
        externalReference: resource.external_reference,
    };
}

/** "Makelaar · 0118 - 61 22 90" - de contactregel onder de naam. */
export function formatContactLine(client: Client): string {
    return [client.typeLabel, client.contactName, client.phone].filter(Boolean).join(' · ');
}

/**
 * "Nieuwe Haven 18A, 4331 KR Middelburg", of `null` als er geen adres bekend is.
 * Een toevoeging die met een cijfer begint staat achter een streepje (`12-2`),
 * zoals de BAG hem schrijft; `122` is een ander huis. Een Nederlandse postcode
 * staat er als `4331 KR`, ook als hij anders getypt is.
 */
export function formatClientAddress(address: ClientAddress): string | null {
    const suffix = address.suffix
        ? `${/^\d/.test(address.suffix) ? '-' : ''}${address.suffix}`
        : '';
    const street = [address.street, address.number ? `${address.number}${suffix}` : suffix]
        .filter(Boolean)
        .join(' ');
    const postcode = address.postcode?.replace(
        /^\s*([1-9]\d{3})\s*([a-zA-Z]{2})\s*$/,
        (_match, digits: string, letters: string) => `${digits} ${letters.toUpperCase()}`,
    );
    const place = [postcode, address.city].filter(Boolean).join(' ');
    const line = [street, place].filter(Boolean).join(', ');

    return line === '' ? null : line;
}

/**
 * Het huisnummer als de cijfers en wat erachter komt, zonder spaties, streepjes of
 * kleine letters: `14a` is `14` met toevoeging `A`, maar `122` is niet `12-2`.
 */
function houseNumberKey(address: ClientAddress): string {
    const typed = (address.number ?? '').trim();
    const match = /^(\d+)(.*)$/.exec(typed);
    const rest = `${match?.[2] ?? ''}${address.suffix ?? ''}`.replace(/[^A-Za-z0-9]/g, '');

    return `${match?.[1] ?? typed}|${rest.toUpperCase()}`;
}

/**
 * Staat er hetzelfde adres? Zoals de API het opslaat: een postcode zonder spatie,
 * een toevoeging in kleine letters of in het huisnummerveld is geen ander adres.
 */
export function sameClientAddress(a: ClientAddress, b: ClientAddress): boolean {
    const compact = (value: string | null) => (value ?? '').replace(/\s+/g, '').toUpperCase();
    const trimmed = (value: string | null) => (value ?? '').trim();

    return (
        trimmed(a.street) === trimmed(b.street) &&
        houseNumberKey(a) === houseNumberKey(b) &&
        compact(a.postcode) === compact(b.postcode) &&
        trimmed(a.city) === trimmed(b.city)
    );
}

/**
 * Wat "Klant bewerken" naar `PATCH /clients/{client}` stuurt. Naam en contact zijn
 * tekst zoals ze in het formulier staan; een leeg veld maakt het gegeven leeg,
 * behalve de naam: die is verplicht. Het adres komt uit het adresblok, met `null`
 * voor wat er niet is.
 */
export interface ClientChanges {
    name: string;
    contactName: string;
    email: string;
    phone: string;
    address: ClientAddress;
}

/** Wat `POST /clients` nodig heeft om een nieuwe opdrachtgever te maken. */
export interface NewClient {
    type: ClientType;
    name: string;
    contactName?: string;
    email?: string;
    phone?: string;
    /** Het huidige adres van de klant; zonder adres blijft het leeg. */
    address?: ClientAddress;
}
